import { handleScreenshot } from "../utils/fileUpload";
import { addPosition, pixelToGridPosition } from "../utils/gridPosition";
import { useCallback, useEffect } from "react";
import { AnyBlock } from "../types";
import { useScrollRef, useCanvasRef } from "../jotai/atoms";
import { Spring } from "wobble";
import { useFeatureFlags } from "../roomservice/maps";

export function useCaptureStickerTrigger(cb: Function, isSelf: boolean) {
  let canvasRef = useCanvasRef();
  let [featureFlags] = useFeatureFlags();
  useEffect(() => {
    if (!canvasRef || !isSelf) return;
    function handle(e: MouseEvent) {
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      cb();
    }
    if (featureFlags?.faceSticker) {
      canvasRef.addEventListener("dblclick", handle);
    }

    return () => {
      if (isSelf && canvasRef) {
        canvasRef.removeEventListener("dblclick", handle);
      }
    };
  }, [canvasRef, cb, featureFlags?.faceSticker, isSelf]);
}
export function useCaptureSticker(
  upsertBlock: (block: AnyBlock) => void,
  video: HTMLVideoElement | null,
  circleProgress: Spring
) {
  const scroll = useScrollRef();

  return useCallback(() => {
    if (!video) return;
    const isCircle = circleProgress.currentValue === 0;
    const canvas = document.createElement("canvas");
    // scale the canvas accordingly
    let { left, top, width, height } = video.getBoundingClientRect();

    let aspectRatio = video.videoHeight / video.videoWidth;
    if (width > height) {
      canvas.width = width;
      canvas.height = width * aspectRatio;
    } else {
      canvas.width = height / aspectRatio;
      canvas.height = height;
    }

    // draw the video at that frame
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new TypeError("failed to create context");
    }
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    if (isCircle) {
      ctx.beginPath();
      ctx.arc(
        canvas.width / 2,
        canvas.height / 2,
        Math.min(canvas.height, canvas.width) / 2,
        0,
        Math.PI * 2
      );
      ctx.clip();
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!scroll?.current) return;
        if (blob) {
          handleScreenshot(
            blob,
            upsertBlock,
            pixelToGridPosition(
              addPosition(
                {
                  x: left + (isCircle ? 0 : width), // push facedock photos to the right
                  y: top,
                },
                scroll.current
              )
            ),
            { width, height }
          );
        }
      },
      // use png when transparency is needed
      isCircle ? "image/png" : "image/jpg"
    );
  }, [upsertBlock, circleProgress, video, scroll]);
}
